/**
 * CE R2: pure, N-level brand-kit inheritance resolution.
 *
 * The chain is ordered ROOT → LEAF (corporate parent first, franchise location
 * last). A child overrides its ancestors field-by-field — EXCEPT for fields an
 * ancestor has locked, where the ancestor's value wins and the child's override
 * is discarded (Klaviyo-style true lock-and-inherit, not clone-and-drift).
 *
 * This is the shared core the `inherit_brand_kit` agent tool delegates to; the
 * 2-element chain [corporate, location] reproduces the original 2-level merge.
 * Pure (no I/O) so it is trivially unit-testable.
 */

/** A brand kit as consumed by the resolver (camelCase, I/O-agnostic). */
export interface ResolvableBrandKit {
  id: string;
  name?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  logoUrl?: string | null;
  disclaimerText?: string | null;
  fontFamily?: string | null;
  brandGuidelines?: Record<string, unknown> | null;
  voiceGuidelines?: string[] | null;
  /** Field keys this kit locks for its DESCENDANTS (camelCase or snake_case). */
  lockedFields?: string[] | null;
  parentKitId?: string | null;
  organizationId?: string | null;
}

export interface ResolvedBrandKit {
  id: string;
  name: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  logoUrl: string | null;
  disclaimerText: string | null;
  fontFamily: string | null;
  brandGuidelines: Record<string, unknown> | null;
  voiceGuidelines: string[];
  /** Accumulated locked field keys (normalized camelCase) across the chain. */
  lockedFields: string[];
  parentKitId: string | null;
  organizationId: string | null;
}

/** Scalar/object fields that inherit + can be locked. `voiceGuidelines` is
 * handled separately because it accumulates (union) rather than overrides. */
const MERGEABLE_FIELDS = [
  'name',
  'primaryColor',
  'secondaryColor',
  'accentColor',
  'logoUrl',
  'disclaimerText',
  'fontFamily',
  'brandGuidelines',
] as const;

const SNAKE_TO_CAMEL: Record<string, string> = {
  primary_color: 'primaryColor',
  secondary_color: 'secondaryColor',
  accent_color: 'accentColor',
  logo_url: 'logoUrl',
  disclaimer_text: 'disclaimerText',
  font_family: 'fontFamily',
  brand_guidelines: 'brandGuidelines',
};

/** Normalize a lock key to the resolver's camelCase convention. Accepts keys
 * already in camelCase (pass-through) and legacy snake_case from the DB. */
export function normalizeLockKey(key: string): string {
  return SNAKE_TO_CAMEL[key] ?? key;
}

/**
 * Resolve an ordered (root→leaf) brand-kit chain into one effective kit.
 * @throws if the chain is empty.
 */
export function resolveBrandKitChain(chain: ResolvableBrandKit[]): ResolvedBrandKit {
  if (chain.length === 0) {
    throw new Error('Cannot resolve an empty brand kit chain');
  }

  const root = chain[0];
  const resolved: Record<string, unknown> = {};
  for (const field of MERGEABLE_FIELDS) {
    resolved[field] = root[field] ?? null;
  }
  let voice: string[] = [...(root.voiceGuidelines ?? [])];
  const locked = new Set<string>((root.lockedFields ?? []).map(normalizeLockKey));

  for (let i = 1; i < chain.length; i++) {
    const kit = chain[i];
    for (const field of MERGEABLE_FIELDS) {
      if (locked.has(field)) continue; // ancestor-locked → discard child override
      const value = kit[field];
      if (value !== null && value !== undefined) {
        resolved[field] = value;
      }
    }
    if (kit.voiceGuidelines?.length) {
      voice = Array.from(new Set([...voice, ...kit.voiceGuidelines]));
    }
    // A kit locks fields for its descendants only — apply AFTER its own overrides.
    for (const key of kit.lockedFields ?? []) locked.add(normalizeLockKey(key));
  }

  const leaf = chain[chain.length - 1];
  return {
    id: leaf.id,
    name: (resolved.name as string | null) ?? null,
    primaryColor: (resolved.primaryColor as string | null) ?? null,
    secondaryColor: (resolved.secondaryColor as string | null) ?? null,
    accentColor: (resolved.accentColor as string | null) ?? null,
    logoUrl: (resolved.logoUrl as string | null) ?? null,
    disclaimerText: (resolved.disclaimerText as string | null) ?? null,
    fontFamily: (resolved.fontFamily as string | null) ?? null,
    brandGuidelines: (resolved.brandGuidelines as Record<string, unknown> | null) ?? null,
    voiceGuidelines: voice,
    lockedFields: Array.from(locked),
    parentKitId: leaf.parentKitId ?? null,
    organizationId: leaf.organizationId ?? null,
  };
}
